import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import noAvailableImg from '../../assets/img/noFarmerImg.png';
import arrowSvg from '../../assets/svg/arrow-back.svg';
import calendarSvg from '../../assets/svg/calendar.svg';
import locationSvg from '../../assets/svg/location.svg';
import { formatDate, isValidDate } from '../../constants/formatDate';

const Container = styled.div`
  cursor: pointer;
  width: 400px;
  height: 100%;
  border: 6px solid #e5007d;
  background-color: #e5007d;
  color: #fff;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s;
  display: flex;
  flex-direction: column;
  justify-content: start;

  &:hover {
    transform: translateY(-5px);
  }

  @media (max-width: 800px) {
    width: 100%;
    border-radius: 0;
    flex-direction: column-reverse;
  }
`;

const FarmerImage = styled.img`
  width: 100%;
  height: 260px;
  object-fit: contain;
  object-position: center;

  @media (max-width: 1400px) {
    height: 200px;
  }
  @media (max-width: 800px) {
    height: 300px;
  }
`;

export const FarmerInfo = styled.div`
  padding: 15px;
`;

export const FarmerName = styled.h3`
  text-transform: uppercase;
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 10px;
`;

export const Box = styled.div`
  display: flex;
  justify-content: start;
  gap: 20px;

  img {
    filter: brightness(0) saturate(100%) invert(100%);
  }
`;

export const FarmerDetails = styled.div`
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 5px;
  border-right: 1px solid #d9d6cf;
  padding-right: 30px;
  &:last-child {
    border: none;
    padding-right: 0;
  }
  p {
    margin: 0;
  }

  @media (max-width: 1400px) {
    padding-right: 10px;

    p {
      font-size: 10px;
    }
  }
`;

const WrapperFarmerInfo = styled.div`
  display: flex;
  justify-content: start;
  align-items: center;
  gap: 20px;
`;

const BackButton = styled.div`
  display: none;
  width: 60px;
  height: 60px;
  justify-content: center;
  border-radius: 10px;
  align-items: center;
  background-color: #00000033;

  img {
    width: 40px;
    height: 40px;
    filter: brightness(0) saturate(100%) invert(100%);
  }

  @media (max-width: 800px) {
    display: flex;
  }
`;

const dotsAnimation = keyframes`
  20% {
    background-position: 0% 0%, 50% 50%, 100% 50%;
  }
  40% {
    background-position: 0% 100%, 50% 0%, 100% 50%;
  }
  60% {
    background-position: 0% 50%, 50% 100%, 100% 0%;
  }
  80% {
    background-position: 0% 50%, 50% 50%, 100% 100%;
  }
`;

export const Dots = styled.div`
  position: absolute;
  width: 44px;
  height: 21.1px;
  background:
    radial-gradient(circle closest-side, #fff 90%, #0000) 0% 50%,
    radial-gradient(circle closest-side, #fff 90%, #0000) 50% 50%,
    radial-gradient(circle closest-side, #fff 90%, #0000) 100% 50%;
  background-size: calc(100% / 3) 10px;
  background-repeat: no-repeat;
  animation: ${dotsAnimation} 0.8s infinite linear;
`;

export const Wrapper = styled.div`
  position: relative;
  top: 30%;
  left: 45%;
`;

interface FarmerCardProps {
  data: any;
  onClose?: (data: any) => void;
}

export const MobileFarmerCard: React.FC<FarmerCardProps> = ({ data, onClose }) => {
  const [isImageLoading, setIsImageLoading] = useState(true);
  const [imageSrc, setImageSrc] = useState(data?.profile_photo ?? noAvailableImg);

  if (!data) {
    return <p>Loading...</p>;
  }

  const fullName =
    data.first_name && data.last_name ? `${data.first_name} ${data.last_name}` : 'Farmer Name';

  return (
    <Container>
      {isImageLoading && (
        <Wrapper>
          <Dots />
        </Wrapper>
      )}
      <FarmerImage
        src={imageSrc}
        onLoad={() => setIsImageLoading(false)}
        onError={() => setImageSrc(noAvailableImg)}
      />
      <WrapperFarmerInfo>
        <BackButton>
          <img src={arrowSvg} onClick={onClose} />
        </BackButton>
        <FarmerInfo>
          <FarmerName>{fullName}</FarmerName>
          <Box>
            <FarmerDetails>
              <img src={locationSvg} />
              <p>{data.farmer_group}</p>
            </FarmerDetails>
            {isValidDate(data.registration_date) && (
              <FarmerDetails>
                <img src={calendarSvg} />
                <p>Since {formatDate(data.registration_date)}</p>
              </FarmerDetails>
            )}
          </Box>
        </FarmerInfo>
      </WrapperFarmerInfo>
    </Container>
  );
};
